import React, { useEffect, useState, useRef } from "react";
import SingleInput, {
  SingleInputRef
} from "@/components/controls/SingleInput";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { useTranslation } from "react-i18next";

const TRANSLATION_KEY = "settings.product";

interface EditablePriceCellProps {
  value: number;
  onValueChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
  "data-test"?: string;
}

export const EditablePriceCell: React.FC<EditablePriceCellProps> = ({
  value,
  onValueChange,
  disabled = false,
  className,
  "data-test": dataTest
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const editableInputRef = useRef<SingleInputRef>(null);

  const handleSave = (newValue: string) => {
    const numericValue = parseFloat(newValue) || 0;
    onValueChange(numericValue);
    setIsEditing(false);
  };

  // Unsigned: the field's guard admits digits, commas and dots only, so a "$"
  // here was rejected on the first keystroke and saved 0.
  const formatEditableValue = () => {
    if (value === 0) return "";
    return value.toFixed(2);
  };

  // Signed: the read-only branch below renders its own text instead of going
  // through SingleInput's collapsed view, so nothing else adds the sign back.
  const formatReadOnlyValue = () => {
    if (value === 0) return "";
    return `$${value.toFixed(2)}`;
  };

  const handleCellClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  // Keyed on isEditing rather than called from the click handler: the branch
  // holding editableInputRef has not rendered at click time, so the ref was
  // null and the first click never opened the editor.
  useEffect(() => {
    if (isEditing) {
      editableInputRef.current?.startEditing();
    }
  }, [isEditing]);

  const handlePriceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleCellClick();
  };

  if (value === 0) {
    return (
      <div className={cn("w-[140px]", className)} data-test={dataTest}>
        <SingleInput
          ref={editableInputRef}
          fieldName="price-input"
          initialValue={formatEditableValue()}
          onSave={handleSave}
          isMonetary={true}
          currencySymbol="$"
          placeholder={t(`${TRANSLATION_KEY}.editablePriceCell.price`)}
          disabled={disabled}
          textVariant="body"
          containerClassName=""
        />
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className={cn("w-[140px]", className)} data-test={dataTest}>
        <SingleInput
          ref={editableInputRef}
          fieldName="price-input"
          initialValue={formatEditableValue()}
          onSave={handleSave}
          isMonetary={true}
          currencySymbol="$"
          placeholder={t(`${TRANSLATION_KEY}.editablePriceCell.price`)}
          disabled={disabled}
          textVariant="body"
        />
      </div>
    );
  }

  return (
    <div
      className={cn("w-[140px] cursor-pointer", className)}
      onClick={handleCellClick}
      data-test={dataTest}
    >
      <div className="inline-block p-2">
        <div onClick={handlePriceClick}>
          <Text
            variant="body"
            className="hover:bg-surface-hover-strong rounded px-1"
          >
            {formatReadOnlyValue()}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default EditablePriceCell;
